/**
 * Live presence on a workflow's chart (AMO-178): who else has it open, and
 * their pointer, the step they have selected, a step they drag, a link they
 * draw and a step they bring out of the Steps panel, all in the chart's own
 * coordinates. Nothing here is an edit: a dragged step's drop is saved as
 * usual and reaches the others as a new version.
 *
 * One room per chart: the editing chart of a workflow (`runId` null), or one
 * run's chart. Only people who may see that chart join it; a Space's client
 * guests never do. The server names each person (their name and colour) and
 * gives them an id that only says two connections are the same person.
 *
 * A workflow's editing room also carries every saved version as a
 * `revision`, to every chart in it, the saver's own and those that may only
 * run it included. A chart that missed some asks for them with
 * `workflows.revisionsSince` on the same subscription. It carries eve's
 * requests on the workflow too, whole, in `sync` and on every change, so a
 * chart opened mid-request knows as much as one open from the start.
 *
 * Wire shapes only: the rooms live in `apps/server/src/amoreg/workflows/presence.ts`.
 *
 * @module workflowPresence
 */
import * as Schema from "effect/Schema";

import { IsoDateTime, NonNegativeInt } from "./baseSchemas.ts";
import { WorkflowEdge, WorkflowElementId, WorkflowGraph, WorkflowNode, WorkflowPosition } from "./workflows.ts";

const PresenceId = Schema.String.check(Schema.isMaxLength(64));

/** Which chart: a workflow's editing chart (`runId` null) or one run's. */
export const WorkflowPresenceRef = Schema.Struct({
  organizationId: PresenceId,
  workflowId: PresenceId,
  runId: Schema.NullOr(PresenceId),
});
export type WorkflowPresenceRef = typeof WorkflowPresenceRef.Type;

/** What one person is doing on the chart. */
export const WorkflowPresenceState = Schema.Struct({
  /** Their pointer; null when it is off the chart. */
  cursor: Schema.NullOr(WorkflowPosition),
  /** The step they have selected. */
  selection: Schema.NullOr(PresenceId),
  /** A step they are dragging, where they have it. It is theirs until they let go. */
  drag: Schema.NullOr(Schema.Struct({ id: PresenceId, at: WorkflowPosition })),
  /** A link they are drawing: from a step's outlet (null: not chosen yet) to their pointer, over a step or not. */
  link: Schema.NullOr(
    Schema.Struct({
      source: PresenceId,
      port: Schema.NullOr(Schema.Literals(["out", "yes", "no"])),
      pointer: WorkflowPosition,
      target: Schema.NullOr(PresenceId),
    }),
  ),
  /** A step they are bringing out of the Steps panel: its kind, and where it would land. */
  adding: Schema.NullOr(Schema.Struct({ kind: PresenceId, at: WorkflowPosition })),
  /**
   * The step field they are typing in: `label` or `config.<key>`, as a
   * revision's diff names it. Eve's edits to it are refused while it is
   * held. Optional: a chart from before it existed sends none.
   */
  editing: Schema.optional(Schema.NullOr(Schema.Struct({ stepId: PresenceId, field: Schema.String.check(Schema.isMaxLength(128)) }))),
});
export type WorkflowPresenceState = typeof WorkflowPresenceState.Type;

/** Someone on the chart, as the server names them. */
export const WorkflowPeer = Schema.Struct({
  /** One open chart of theirs (a tab); it changes when they reconnect. */
  connection: Schema.String,
  /** The same for every connection of one person in this room, and meaningless outside it. */
  person: Schema.String,
  name: Schema.String,
  /** `#rrggbb`, the same colour they have everywhere. */
  color: Schema.String,
  state: WorkflowPresenceState,
});
export type WorkflowPeer = typeof WorkflowPeer.Type;


/** Someone arrived, or did something. */
export const WorkflowPresencePeerEvent = Schema.Struct({
  type: Schema.Literal("peer"),
  peer: WorkflowPeer,
});

export const WorkflowPresenceLeftEvent = Schema.Struct({
  type: Schema.Literal("left"),
  connection: Schema.String,
});

/** The chart is no longer this person's to see (its sharing changed); nothing follows. */
export const WorkflowPresenceClosedEvent = Schema.Struct({
  type: Schema.Literal("closed"),
  reason: Schema.Literals(["access_lost"]),
});

/** The longest group, `reverts` or `saveId` a revision carries. */
export const WORKFLOW_REVISION_KEY_MAX = 128;

const RevisionKey = Schema.String.check(Schema.isMaxLength(WORKFLOW_REVISION_KEY_MAX));

/** A workflow's content: what a revision holds. */
export const WorkflowRevisionContent = Schema.Struct({
  graph: WorkflowGraph,
  name: Schema.String,
  description: Schema.NullOr(Schema.String),
});
export type WorkflowRevisionContent = typeof WorkflowRevisionContent.Type;

/**
 * How a revision changed its parent: whole steps and links set or removed,
 * and the layout, name and description when they changed. The shape
 * `makeContentPatch` in `@repo/shared/workflowDiff` makes.
 */
export const WorkflowContentPatch = Schema.Struct({
  nodes: Schema.Struct({ set: Schema.Array(WorkflowNode), remove: Schema.Array(RevisionKey) }),
  edges: Schema.Struct({ set: Schema.Array(WorkflowEdge), remove: Schema.Array(RevisionKey) }),
  /** Null when the layout was removed. */
  layout: Schema.optionalKey(Schema.NullOr(Schema.Literal("down"))),
  name: Schema.optionalKey(Schema.String),
  description: Schema.optionalKey(Schema.NullOr(Schema.String)),
});
export type WorkflowContentPatch = typeof WorkflowContentPatch.Type;

/** How a revision reaches a chart holding its parent: a patch, or the whole content when a patch cannot say it. */
export const WorkflowContentUpdate = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("patch"), patch: WorkflowContentPatch }),
  Schema.Struct({ kind: Schema.Literal("content"), content: WorkflowRevisionContent }),
]);
export type WorkflowContentUpdate = typeof WorkflowContentUpdate.Type;

const NamedSaver = {
  /** The room's id for the person the save was for, the agent's included: `sync.you` on their own charts. */
  person: Schema.String,
  name: Schema.String,
  color: Schema.String,
};

/** Eve, acting for the named person within one request. */
export const WorkflowRevisionAgent = Schema.Struct({ via: Schema.Literal("agent"), ...NamedSaver });

/**
 * Who saved a revision, named as their presence is, never by email. A
 * session that is not a person's is named as its presence is ("Operator").
 * `backfill` is a version recorded from before revisions existed: nobody is
 * known to have saved it.
 */
export const WorkflowRevisionActor = Schema.Union([
  Schema.Struct({ via: Schema.Literal("person"), ...NamedSaver }),
  WorkflowRevisionAgent,
  Schema.Struct({ via: Schema.Literal("backfill") }),
]);
export type WorkflowRevisionActor = typeof WorkflowRevisionActor.Type;

/** One saved version of a workflow; its parent is `version - 1`. */
export const WorkflowRevision = Schema.Struct({
  version: NonNegativeInt,
  at: IsoDateTime,
  actor: WorkflowRevisionActor,
  /** The agent request it was saved in; null for a person's own save. */
  group: Schema.NullOr(RevisionKey),
  /** The request this revision took back (an Undo); null otherwise. */
  reverts: Schema.NullOr(RevisionKey),
  /** The key the saver sent with it: their own chart knows its save by it. */
  saveId: Schema.NullOr(RevisionKey),
  change: WorkflowContentUpdate,
});
export type WorkflowRevision = typeof WorkflowRevision.Type;

/** A new version, to every chart in the workflow's editing room, the saver's own included. */
export const WorkflowPresenceRevisionEvent = Schema.Struct({
  type: Schema.Literal("revision"),
  revision: WorkflowRevision,
});

/**
 * A step one of eve's requests changed: as it was before the request first
 * changed it and as the request left it, null where it did not exist (added,
 * or removed).
 */
export const WorkflowRequestStep = Schema.Struct({
  id: WorkflowElementId,
  before: Schema.NullOr(WorkflowNode),
  after: Schema.NullOr(WorkflowNode),
});
export type WorkflowRequestStep = typeof WorkflowRequestStep.Type;

/**
 * One of eve's requests that saved into this workflow, as the server knows
 * it. `steps` are the ids of the steps it added, removed or edited, first
 * changed first (`stepChangesOf` in `@repo/shared/workflowDiff`); a step
 * that only gained or lost a link is not one, unless the request changed
 * links and nothing else, when they are the steps those links leave.
 * `changes` holds the same steps as they were and as the request left them;
 * absent from a server that does not say. Its Undo takes back the whole
 * request, links included. `awaiting-approval`: eve waits for
 * the person's OK on a confirmation card. `ended`: its turn is over; it stays
 * until it is undone, until the same person's next request saves here, or
 * for a day.
 */
export const WorkflowAgentRequest = Schema.Struct({
  group: RevisionKey,
  actor: WorkflowRevisionAgent,
  steps: Schema.Array(Schema.String),
  changes: Schema.optionalKey(Schema.Array(WorkflowRequestStep)),
  state: Schema.Literals(["editing", "awaiting-approval", "ended"]),
});
export type WorkflowAgentRequest = typeof WorkflowAgentRequest.Type;

/**
 * First on every subscription (and again when it fell behind): this
 * connection's key, its own `person` id, and everyone else here. `version`
 * is the workflow's saved version as the room knows it; a chart behind it
 * catches up. Null on a run's chart, which no revision reaches. `requests`:
 * eve's requests on the workflow, none on a run's chart; absent from a
 * server that does not say.
 */
export const WorkflowPresenceSyncEvent = Schema.Struct({
  type: Schema.Literal("sync"),
  connection: Schema.String,
  you: Schema.String,
  version: Schema.NullOr(NonNegativeInt),
  peers: Schema.Array(WorkflowPeer),
  requests: Schema.optionalKey(Schema.Array(WorkflowAgentRequest)),
});

/** Eve's requests on the workflow changed: all of them, as they are now. */
export const WorkflowPresenceRequestsEvent = Schema.Struct({
  type: Schema.Literal("requests"),
  requests: Schema.Array(WorkflowAgentRequest),
});

export const WorkflowPresenceStreamEvent = Schema.Union([
  WorkflowPresenceSyncEvent,
  WorkflowPresencePeerEvent,
  WorkflowPresenceLeftEvent,
  WorkflowPresenceClosedEvent,
  WorkflowPresenceRevisionEvent,
  WorkflowPresenceRequestsEvent,
]);
export type WorkflowPresenceStreamEvent = typeof WorkflowPresenceStreamEvent.Type;

export const WorkflowPresencePushInput = Schema.Struct({
  ...WorkflowPresenceRef.fields,
  /** The `sync` event's key: presence rides on that subscription. */
  connection: Schema.String.check(Schema.isMaxLength(64)),
  state: WorkflowPresenceState,
});
export type WorkflowPresencePushInput = typeof WorkflowPresencePushInput.Type;

/** Catch-up on one of this connection's editing-chart subscriptions, and on its authorization. */
export const WorkflowRevisionsSinceInput = Schema.Struct({
  organizationId: PresenceId,
  workflowId: PresenceId,
  /** The `sync` event's key. */
  connection: Schema.String.check(Schema.isMaxLength(64)),
  /** The last version the chart holds. */
  since: NonNegativeInt,
});
export type WorkflowRevisionsSinceInput = typeof WorkflowRevisionsSinceInput.Type;

/**
 * The versions after `since`, in order, up to the head; or the head alone
 * when the chart is too far behind or some of those versions have no
 * revision. A chart that takes the head plays no cues.
 */
export const WorkflowRevisionsSinceResult = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("revisions"), revisions: Schema.Array(WorkflowRevision) }),
  Schema.Struct({ kind: Schema.Literal("head"), version: NonNegativeInt, content: WorkflowRevisionContent }),
]);
export type WorkflowRevisionsSinceResult = typeof WorkflowRevisionsSinceResult.Type;

/** The chart does not exist, or is not this person's to see. */
export class WorkflowPresenceNotFoundError extends Schema.TaggedErrorClass<WorkflowPresenceNotFoundError>()(
  "WorkflowPresenceNotFoundError",
  {},
) {
  override get message(): string {
    return "Workflow not found.";
  }
}

export class WorkflowPresenceRateLimitedError extends Schema.TaggedErrorClass<WorkflowPresenceRateLimitedError>()(
  "WorkflowPresenceRateLimitedError",
  { retryAfterMs: Schema.Int },
) {
  override get message(): string {
    return "Too many charts opened at once. Try again shortly.";
  }
}

export class WorkflowPresenceUnavailableError extends Schema.TaggedErrorClass<WorkflowPresenceUnavailableError>()(
  "WorkflowPresenceUnavailableError",
  {},
) {
  override get message(): string {
    return "Who else is here cannot be shown right now.";
  }
}

export const WorkflowPresenceError = Schema.Union([
  WorkflowPresenceNotFoundError,
  WorkflowPresenceRateLimitedError,
  WorkflowPresenceUnavailableError,
]);
export type WorkflowPresenceError = typeof WorkflowPresenceError.Type;

export class WorkflowCatchUpRateLimitedError extends Schema.TaggedErrorClass<WorkflowCatchUpRateLimitedError>()(
  "WorkflowCatchUpRateLimitedError",
  { retryAfterMs: Schema.Int },
) {
  override get message(): string {
    return "Too many requests for missed changes. Try again shortly.";
  }
}

export class WorkflowCatchUpUnavailableError extends Schema.TaggedErrorClass<WorkflowCatchUpUnavailableError>()(
  "WorkflowCatchUpUnavailableError",
  {},
) {
  override get message(): string {
    return "Missed changes cannot be loaded right now.";
  }
}

/** Why `workflows.revisionsSince` refused: the chart is not this connection's, too many asks, or an outage. */
export const WorkflowCatchUpError = Schema.Union([
  WorkflowPresenceNotFoundError,
  WorkflowCatchUpRateLimitedError,
  WorkflowCatchUpUnavailableError,
]);
export type WorkflowCatchUpError = typeof WorkflowCatchUpError.Type;

/** A request the person can take back or bring back: the group it was saved as, and the newest version it saved. */
export const WorkflowRequestPointer = Schema.Struct({ group: RevisionKey, version: NonNegativeInt });
export type WorkflowRequestPointer = typeof WorkflowRequestPointer.Type;

/**
 * One person's Undo and Redo on one workflow, kept by the server so a reload
 * keeps them. `undo` is the newest agent request made for them, or Redo of
 * theirs, still in effect; `redo` is their newest Undo, until eve makes a
 * newer request for them.
 */
export const WorkflowRequestPointers = Schema.Struct({
  undo: Schema.NullOr(WorkflowRequestPointer),
  redo: Schema.NullOr(WorkflowRequestPointer),
});
export type WorkflowRequestPointers = typeof WorkflowRequestPointers.Type;

/** Take back one group: an agent request or a Redo (`/undo`), or an Undo (`/redo`). */
export const WorkflowUndoRequest = Schema.Struct({ group: RevisionKey, saveId: RevisionKey });
export type WorkflowUndoRequest = typeof WorkflowUndoRequest.Type;

const RefusedStepEnd = Schema.Struct({ stepId: Schema.String, stepLabel: Schema.NullOr(Schema.String) });

/** What a refused Undo names: a step's field, a link by both its ends, or the workflow itself (`stepId` null). */
export const WorkflowRefusedStep = Schema.Struct({
  stepId: Schema.NullOr(Schema.String),
  stepLabel: Schema.NullOr(Schema.String),
  /** `step` (its existence or kind), `label`, `config.<key>`, `link`, `name` or `description`. */
  field: Schema.String,
  /** A link's other end. */
  target: Schema.optional(RefusedStepEnd),
});
export type WorkflowRefusedStep = typeof WorkflowRefusedStep.Type;

/**
 * Why nothing was undone. `changed-since`: someone else changed what the
 * group changed, every such step named. `new-problem`: the result would put
 * the named steps on a loop or give two of them one name. `over-limit`: the
 * result would hold too many steps or links. `no-earlier-version`: the
 * group made the workflow, or the version before it was never recorded.
 */
export const WorkflowUndoRefusal = Schema.Literals(["changed-since", "new-problem", "over-limit", "no-earlier-version"]);
export type WorkflowUndoRefusal = typeof WorkflowUndoRefusal.Type;

/** An Undo or Redo's answer, with the person's pointers after it. All or nothing: a refusal changed nothing. */
export const WorkflowUndoResult = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("reverted"), version: NonNegativeInt, pointers: WorkflowRequestPointers }),
  Schema.Struct({
    kind: Schema.Literal("refused"),
    reason: WorkflowUndoRefusal,
    steps: Schema.Array(WorkflowRefusedStep),
    pointers: WorkflowRequestPointers,
  }),
  Schema.Struct({ kind: Schema.Literal("already-undone"), pointers: WorkflowRequestPointers }),
]);
export type WorkflowUndoResult = typeof WorkflowUndoResult.Type;
